import { FC, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { Table, TableHead, TableRow, TableCell, TableBody, Tooltip, TextField } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';

import { PaperTitle, PaperTitleText, PaperContent } from '../../primitive/Paper';
import { LinkPrefetchReportData } from '../../../../hooks/useLinkPrefetchReport';

interface PageData {
  url: string;
  experimentalPv: number;
  controlDCL: string;
  controlLCP: string;
  testDCL: string;
  testLCP: string;
  dclImprovement: string;
  lcpImprovement: string;
}

type Props = {
  reportData: LinkPrefetchReportData;
};

const ReportTable: FC<Props> = ({ reportData }) => {
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const pvThreshold = 1000;

  // reportData.patterns のキーを PV 降順に抽出 (サイト全体平均の "" は除外)
  const patterns = Object.keys(reportData.patterns || {})
    .filter((pattern) => pattern != '')
    .sort((a, b) => reportData.patterns[b].experimental.pv - reportData.patterns[a].experimental.pv);
  const calculateImprovementPercentage = (control: number, test: number): string => {
    if (control === 0) {
      return 'N/A';
    }
    const improvement = ((test - control) / control) * 100;
    const rounded = Math.round(improvement);
    return rounded > 0 ? `+${rounded}%` : `${rounded}%`;
  };

  const allPageData: PageData[] = patterns.map((pattern) => {
    const data = reportData.patterns[pattern];
    return {
      url: pattern,
      experimentalPv: data.experimental.pv,
      controlDCL: `${(data.control.dcl / 1000).toFixed(2)}秒`,
      controlLCP: `${(data.control.lcp / 1000).toFixed(2)}秒`,
      testDCL: `${(data.experimental.dcl / 1000).toFixed(2)}秒`,
      testLCP: `${(data.experimental.lcp / 1000).toFixed(2)}秒`,
      dclImprovement: calculateImprovementPercentage(data.control.dcl, data.experimental.dcl),
      lcpImprovement: calculateImprovementPercentage(data.control.lcp, data.experimental.lcp),
    };
  });

  const filteredPageData = useMemo(() => {
    return allPageData.filter((page) => page.url.toLowerCase().includes(searchText.toLowerCase()));
  }, [allPageData, searchText]);

  const totalItems = filteredPageData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedPageData = filteredPageData.slice(startIndex, endIndex);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setCurrentPage(1); // 検索時はページを1にリセット
  };

  const getImprovementColor = (improvement: string) => {
    if (improvement.startsWith('-')) return '#4CAF50';
    if (improvement.startsWith('+')) return '#F44336';
    return '#FFC107';
  };

  return (
    <Container>
      <PaperTitle>
        <PaperTitleText>ページ別A/Bテスト結果</PaperTitleText>
      </PaperTitle>
      <PaperContent>
        <SearchContainer>
          <TextField
            placeholder="ページURLで検索..."
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            style={{ width: '400px' }}
          />
        </SearchContainer>
        <PaginationContainer>
          <PaginationInfo>
            全{totalItems}件中 {totalItems > 0 ? startIndex + 1 : 0}-{endIndex}件を表示 (有効PV降順)
          </PaginationInfo>
          <PaginationButtons>
            <PageButton onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              ≪
            </PageButton>
            <PageButton onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
              ＜
            </PageButton>
            <PageInfo>
              {currentPage} / {totalPages || 1}
            </PageInfo>
            <PageButton onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages}>
              ＞
            </PageButton>
            <PageButton onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>
              ≫
            </PageButton>
          </PaginationButtons>
        </PaginationContainer>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>
                <UrlWithPv>
                  ページURL
                  <Tooltip title="速度改善ツールが起動して正常計測されたページビュー数です。対象OS・ブラウザでのアクセス時にのみカウントされ、異常値は除外されます。">
                    <PvText>
                      (有効PV
                      <HelpIcon style={{ fontSize: 16, marginBottom: -3, paddingLeft: 3 }} />)
                    </PvText>
                  </Tooltip>
                </UrlWithPv>
                <PatterNoticeText>
                  ※「先読みを有効にするリンクパターン」に設定したパターンごとに表示しています
                </PatterNoticeText>
              </TableCell>
              <TableCell align="center" colSpan={2}>
                未設定グループ (10%)
              </TableCell>
              <TableCell rowSpan={2} align="center" style={{ width: '2%' }}></TableCell>
              <TableCell align="center" colSpan={2}>
                設定グループ (90%)
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center" style={{ width: '10%' }}>
                DCL
              </TableCell>
              <TableCell align="center" style={{ width: '10%' }}>
                LCP
              </TableCell>
              <TableCell align="center" style={{ width: '10%', minWidth: '130px' }}>
                DCL
              </TableCell>
              <TableCell align="center" style={{ width: '10%', minWidth: '130px' }}>
                LCP
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPageData.map((page, index) => (
              <TableRow key={index}>
                <TableCell>
                  <UrlWithPv>
                    <UrlText>{page.url}</UrlText>
                    <PvText>({page.experimentalPv.toLocaleString()})</PvText>
                  </UrlWithPv>
                </TableCell>
                <TableCell align="center">{page.controlDCL}</TableCell>
                <TableCell align="center">{page.controlLCP}</TableCell>
                <TableCell align="center">→</TableCell>
                <TableCell align="center">
                  <MetricWithImprovement>
                    <span>{page.testDCL}</span>
                    {
                      page.experimentalPv < pvThreshold ?
                        <Tooltip title="有効PVが不足しています">
                          <div style={{ display: 'flex', paddingLeft: '10px' }}>
                            <NeutralText>データ不足</NeutralText>
                            <HelpIcon style={{ fontSize: 16, marginTop: 5, paddingLeft: 3 }} />
                          </div>
                        </Tooltip>
                        :
                        <ImprovementText color={getImprovementColor(page.dclImprovement)}>
                          {page.dclImprovement}
                        </ImprovementText>
                    }
                  </MetricWithImprovement>
                </TableCell>
                <TableCell align="center">
                  <MetricWithImprovement>
                    <span>{page.testLCP}</span>
                    {
                      page.experimentalPv < pvThreshold ?
                        <Tooltip title="有効PVが不足しています">
                          <div style={{ display: 'flex', paddingLeft: '10px' }}>
                            <NeutralText>データ不足</NeutralText>
                            <HelpIcon style={{ fontSize: 16, marginTop: 5, paddingLeft: 3 }} />
                          </div>
                        </Tooltip>
                        :
                        <ImprovementText color={getImprovementColor(page.lcpImprovement)}>
                          {page.lcpImprovement}
                        </ImprovementText>
                    }
                  </MetricWithImprovement>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </PaperContent>
    </Container>
  );
};

const Container = styled('div')({
  width: '100%',
});

const StyledTable = styled(Table)({
  border: '1px solid #e6e7e8',
  borderRadius: '4px',
  borderCollapse: 'separate',
});

const UrlWithPv = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const UrlText = styled('div')({
  color: '#2196F3',
  fontWeight: 500,
});

const PvText = styled('div')({
  fontSize: '12px',
  color: '#666',
  whiteSpace: 'nowrap',
});

const PatterNoticeText = styled('div')({
  fontSize: '10px',
  color: '#666',
  marginLeft: '4px',
  marginTop: '4px',
})

const MetricWithImprovement = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px',
});

const ImprovementText = styled('div')<{ color: string }>(({ color }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color,
  padding: '2px 6px',
  borderRadius: '4px',
  border: `1px solid ${color}`,
  backgroundColor: color === '#4CAF50' ? '#E8F5E9' : color === '#F44336' ? '#FFEBEE' : '#FFF8E1',
  display: 'inline-block',
}));

const NeutralText = styled('div')({
  fontSize: '10px',
  fontWeight: 600,
  color: '#9E9E9E',
  padding: '2px 6px',
  borderRadius: '4px',
  border: '1px solid #9E9E9E',
  backgroundColor: '#F5F5F5',
  display: 'inline-block',
})

const SearchContainer = styled('div')({
  marginTop: '20px',
  marginBottom: '16px',
});

const PaginationContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
});

const PaginationInfo = styled('div')({
  fontSize: '14px',
  color: '#666',
});

const PaginationButtons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const PageButton = styled('button')<{ disabled?: boolean }>(({ disabled }) => ({
  padding: '4px 12px',
  fontSize: '14px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  backgroundColor: disabled ? '#f5f5f5' : '#fff',
  color: disabled ? '#ccc' : '#333',
  cursor: disabled ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: disabled ? '#f5f5f5' : '#f0f0f0',
  },
}));

const PageInfo = styled('div')({
  padding: '0 12px',
  fontSize: '14px',
  color: '#333',
});

export default ReportTable;
